/**
 * MobileSheet — panneau latéral gauche custom (sans Radix Dialog).
 *
 * Pourquoi ne pas utiliser Radix Dialog ici ?
 * Radix Dialog injecte un `padding-right` sur `body` pour compenser
 * la disparition de la scrollbar lors de l'ouverture. Sur Android
 * (Chrome/WebView), ce décalage peut créer une illusion de viewport
 * élargi et provoquer un scroll horizontal résiduel, même après
 * fermeture. Ce composant évite complètement ce comportement.
 *
 * Architecture :
 * - Overlay : `position: fixed`, couvre tout l'écran, capte les clics extérieurs.
 * - Panel   : `position: fixed`, slide depuis la gauche via CSS transition.
 * - Trap focus + Escape key gérés manuellement pour l'accessibilité.
 * - Aucune injection de style sur `body` ou `html`.
 */

import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

interface MobileSheetProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Largeur du panel (défaut: 84vw, max 340px) */
  width?: string;
}

export function MobileSheet({ open, onClose, children, width = "min(84vw, 340px)" }: MobileSheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Fermeture sur Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Focus trap basique: ramener le focus dans le panel à l'ouverture
  useEffect(() => {
    if (open && panelRef.current) {
      const firstFocusable = panelRef.current.querySelector<HTMLElement>(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
      );
      firstFocusable?.focus();
    }
  }, [open]);

  return (
    <>
      {/* Overlay */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 40,
          background: "var(--color-overlay, rgba(0,0,0,0.5))",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 220ms ease",
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 50,
          width,
          background: "var(--color-background)",
          borderRight: "1px solid var(--gray-4)",
          boxShadow: "var(--shadow-6)",
          overflowY: "auto",
          overflowX: "clip",
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)",
          paddingTop: "max(env(safe-area-inset-top), 0px)",
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </>
  );
}
